/* eslint-disable react/prop-types */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import MapRequestBanner from "../../States/components/MapRequestBanner";
import MapRequestModal from "../../../common/components/MapRequest/MapRequestModal";
import {
  ShieldCheck,
  FileText,
  Map,
  // Brain, // hidden with JaaGa Intelligence
  Gift,
  Search,
  MapPin,
  ChevronDown,
  X,
  ArrowRight,
  Users,
  Landmark,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";
import { useStates } from "../../../hooks/useStates";
import { useStaticPropertyData } from "../../../hooks/useStaticProperties";
import { useCreditsConfig } from "../../../hooks/useCreditsConfig";
import PropertyDocumentCard from "../../state/components/PropertyDocumentCard";
import SampleReportModal from "./SampleReportModal";
import RefferModal from "../../../common/components/ShareModal/RefferModal";
import { loginPopupHandler } from "../../../redux/features/auth/loginSlice";
import { createReferral } from "../../../redux/features/customer/customerActions";
// ── Slide promo images. Swap these files with your branded graphics (keep the
// same names), no code change needed. They live in assets/slides/. ──────────
import auditImg from "../assets/slides/audit.webp";
// import intelImg from "../assets/slides/intelligence.webp"; // hidden with JaaGa Intelligence
import mapsImg from "../assets/slides/maps.webp";
import referImg from "../assets/slides/refer.webp";
import creditsImg from "../assets/slides/credits.webp";
// Used as the hero background on mobile (below 768px).
import mobileHeroImg from "../assets/slides/mobile-hero.webp";

// Bolds the matched part of a document name inside the search suggestions.
const HighlightText = ({ text, query }) => {
  const q = query?.trim();
  if (!q) return <>{text}</>;
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  const lower = q.toLowerCase();
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === lower ? (
          <span key={i} className="psh-suggest-hl">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

// True when a customer auth token is present. Used to pick the credits-slide
// variant immediately, before the profile object has loaded into redux (so a
// logged-in user never briefly sees the logged-out "Join" copy).
const hasAuthToken = () => {
  try {
    const t = localStorage.getItem("token");
    return Boolean(t) && t !== "null" && t !== "undefined";
  } catch {
    return false;
  }
};

// ── Flagship product slides, one full-width card per product ───────────────
const PRODUCTS = [
  {
    id: "audit",
    name: "JaaGa Audit Report",
    badge: "Delivery in 24 hrs",
    title: "Property Audit Report",
    desc: "",
    chips: ["Title clarity check?", "RERA compliance?", "Tax dues flagged?", "40+ govt records"],
    cta: "Get Audit Report",
    to: "/states",
    Icon: ShieldCheck,
    accent: "#22c55e",
    image: auditImg,
    imageAlt: "JaaGa Property Audit Report sample",
    sample: "audit",
    preview: {
      type: "report",
      head: "Property Audit Report",
      badge: "Sample",
      metric: "92",
      unit: "/100",
      metricLabel: "Property Score",
      status: "Safe to buy",
      rows: [
        ["Clear ownership", "Verified", "good"],
        ["No loan on property", "Verified", "good"],
        ["RERA compliance", "Compliant", "good"],
        ["Property tax dues", "Flagged", "warn"],
      ],
    },
  },
  // ── JaaGa Intelligence slide hidden on request (web + mobile). To restore,
  //    uncomment this slide plus the `Brain` and `intelImg` imports above. ──
  /*
  {
    id: "intel",
    name: "JaaGa Intelligence",
    badge: "Live AI · 2.5B+ records",
    title: "JaaGa Intelligence",
    desc: "",
    chips: ["Unsure about title?", "Want a trust score?", "Worried about fraud?", "Buying remotely?"],
    cta: "Explore Intelligence",
    to: "/intelligence",
    Icon: Brain,
    accent: "#a78bfa",
    comingSoon: true,
    image: intelImg,
    imageAlt: "JaaGa Intelligence, search any property",
    preview: {
      type: "report",
      head: "JaaGa Intelligence",
      badge: "Live AI",
      metric: "91",
      unit: "/100",
      metricLabel: "Trust Score",
      status: "Low risk",
      rows: [
        ["Title chain", "Clear · 15 yrs", "good"],
        ["RERA status", "Compliant", "good"],
        ["Market trend", "+14.2% YoY", "accent"],
        ["Docs analyzed", "38", "muted"],
      ],
    },
  },
  */
  {
    id: "maps",
    name: "Survey Maps",
    badge: "Live maps",
    title: "Survey Maps",
    desc: "",
    chips: ["Check plot boundaries?", "Measure area?", "Find survey number?", "Estimate EMI?"],
    cta: "Explore Maps",
    to: "/maps",
    Icon: Map,
    accent: "#38bdf8",
    image: mapsImg,
    imageAlt: "JaaGa Survey Maps, survey maps in seconds",
    sample: "measurement",
    preview: {
      type: "map",
      survey: "29/2",
      village: "Bhoktapur",
      mandal: "Adilabad",
      area: "2.69 ac",
      perimeter: "486 m",
    },
  },
];

const TRUST = [
  { Icon: Users, value: "100K+", label: "Happy Customers" },
  { Icon: FileText, value: "140+", label: "Instant Documents" },
  { Icon: Landmark, value: "Direct from", label: "Government Records" },
];

// ── Product preview mockups that fill the right side of each card ───────────
function ReportPreview({ pv }) {
  return (
    <div className="psh-pv">
      <div className="psh-pv-head">
        <span className="psh-pv-head-icon" style={{ background: "#f0a500" }}>
          <FileText size={14} color="#fff" />
        </span>
        <span className="psh-pv-head-title">{pv.head}</span>
        <span className="psh-pv-badge">{pv.badge}</span>
      </div>
      <div className="psh-pv-top">
        <div className="psh-pv-metric">
          <span className="psh-pv-metric-num" style={{ color: "#16a34a" }}>
            {pv.metric}
          </span>
          {pv.unit && <span className="psh-pv-metric-unit">{pv.unit}</span>}
        </div>
        <div className="psh-pv-metric-side">
          <span className="psh-pv-metric-label">{pv.metricLabel}</span>
          <span className="psh-pv-status">{pv.status}</span>
        </div>
      </div>
      <div className="psh-pv-rows">
        {pv.rows.map(([label, value, tone]) => (
          <div className="psh-pv-row" key={label}>
            <span className="psh-pv-row-left">
              {tone === "warn" ? (
                <AlertTriangle size={13} color="#d97706" />
              ) : tone === "accent" ? (
                <TrendingUp size={13} color="#2563eb" />
              ) : (
                <CheckCircle2 size={13} color="#16a34a" />
              )}
              {label}
            </span>
            <span className={`psh-pv-row-val psh-pv-row-val--${tone}`}>
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MapPreview({ pv }) {
  return (
    <div className="psh-pv psh-pv--map">
      <div className="psh-pv-map-canvas">
        <span className="psh-pv-map-tag" style={{ top: "16%", left: "20%" }}>
          28/4
        </span>
        <span className="psh-pv-map-tag" style={{ top: "30%", right: "14%" }}>
          27
        </span>
        <span className="psh-pv-map-plot" />
        <span className="psh-pv-map-pin">
          <MapPin size={26} fill="#ef4444" color="#fff" />
        </span>
      </div>
      <div className="psh-pv-map-info">
        <div className="psh-pv-map-survey">
          <span className="psh-pv-map-hash">#</span> Survey {pv.survey}
        </div>
        <div className="psh-pv-map-loc">
          Village: <b>{pv.village}</b> · Mandal: <b>{pv.mandal}</b>
        </div>
        <div className="psh-pv-map-area">
          Parcel area <b>{pv.area}</b> · Perimeter {pv.perimeter}
        </div>
      </div>
    </div>
  );
}

function Preview({ p }) {
  return p.preview.type === "map" ? (
    <MapPreview pv={p.preview} />
  ) : (
    <ReportPreview pv={p.preview} />
  );
}

const ProductSearchHero = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [stateOpen, setStateOpen] = useState(false);
  const [docStateOpen, setDocStateOpen] = useState(false);
  const [showSample, setShowSample] = useState(false);
  // Which sample the modal shows ("audit" or "measurement"); kept after close
  // so the content doesn't swap while the modal fades out.
  const [sampleKind, setSampleKind] = useState("audit");

  // ── Real states (same source as the property-documents page) ──
  // Only states flagged `preview: true` are offered — the rest aren't ready to
  // be picked. This list also seeds the default selection below, so the hero
  // opens on a state whose documents actually load.
  const { data: statesRaw = [] } = useStates();
  const { joiningCredits, referralCredits } = useCreditsConfig();
  const states = useMemo(
    () => statesRaw.filter((s) => s?.name && s?.preview === true),
    [statesRaw],
  );
  const [selectedState, setSelectedState] = useState(null);
  useEffect(() => {
    if (!selectedState && states.length) setSelectedState(states[0]);
  }, [states, selectedState]);

  const stateId = selectedState?.id
    ? String(selectedState.id).toLowerCase()
    : "";

  const { data: nonAgri = [] } = useStaticPropertyData(
    stateId,
    "non-agriculture",
  );
  const { data: agri = [] } = useStaticPropertyData(stateId, "agriculture");

  const hasNon = nonAgri.length > 0;
  const hasAgri = agri.length > 0;
  const [activeCategory, setActiveCategory] = useState("non-agriculture");
  // "Request document from map" — the same entry /states and the state pages
  // offer, repeated in this section because it is where a homepage visitor
  // first meets a state's document list.
  const [mapRequestOpen, setMapRequestOpen] = useState(false);
  useEffect(() => {
    if (hasNon) setActiveCategory("non-agriculture");
    else if (hasAgri) setActiveCategory("agriculture");
  }, [stateId, hasNon, hasAgri]);
  const docs = activeCategory === "non-agriculture" ? nonAgri : agri;

  // ── Free-documents / referral slide (login-aware) ──
  // Logged out → promote sign-up + free documents (opens the login popup).
  // Logged in  → promote Refer & Earn (opens the referral share flow).
  const dispatch = useDispatch();
  const { Info: customerInfo, referralCode } = useSelector((s) => s.customer);
  // Subscribing to the login slice makes the slide re-evaluate on login/logout;
  // the token check then resolves the variant even before the profile loads.
  const loginState = useSelector((s) => s.login);
  const isLoggedIn =
    Boolean(customerInfo?._id) || Boolean(loginState?.userId) || hasAuthToken();
  const [referralOpen, setReferralOpen] = useState(false);
  const [referralUrl, setReferralUrl] = useState("");

  const openLogin = () => dispatch(loginPopupHandler(true));
  // Same "Refer a friend" flow as the profile menu: reuse the referral code we
  // already have if present, otherwise generate one via createReferral, then
  // open the shared RefferModal.
  const openReferral = () => {
    if (referralCode) {
      setReferralUrl(`${window.location.origin}?referral=${referralCode}`);
      setReferralOpen(true);
      return;
    }
    dispatch(createReferral()).then((res) => {
      const code = res?.payload?.response?.data?.data?.item?.code;
      if (code) {
        setReferralUrl(`${window.location.origin}?referral=${code}`);
        setReferralOpen(true);
      }
    });
  };

  const creditsSlide = isLoggedIn
    ? {
        id: "credits",
        name: "Refer & Earn",
        badge: "Instant reward · Unlimited",
        image: referImg,
        imageAlt: `Refer & Earn, ${referralCredits} credits per friend`,
        title: `Refer & Earn ${referralCredits} Credits`,
        desc: "",
        chips: ["Share your link", "Friend signs up", `You earn ${referralCredits} credits`, "No cap"],
        cta: "Refer a Friend",
        action: openReferral,
        Icon: Gift,
        accent: "#f0a500",
        preview: {
          type: "report",
          head: "Refer & Earn",
          badge: "Rewards",
          metric: String(referralCredits),
          unit: "",
          metricLabel: "Credits Per Friend",
          status: "Per referral",
          rows: [
            ["Share your referral link", "1 tap", "good"],
            ["Friend signs up", "They join", "good"],
            ["You earn credits", String(referralCredits), "accent"],
            ["Refer more friends", "Unlimited", "good"],
          ],
        },
      }
    : {
        id: "credits",
        name: "Free Property Documents",
        badge: "Free on sign-up",
        image: creditsImg,
        imageAlt: "Free property documents on sign-up",
        title: "Get Free Property Documents",
        desc: "",
        chips: ["Sign up for free", `Get ${joiningCredits} free Credits`, "Download any document", "No credit card required"],
        cta: "Claim Free Documents",
        action: openLogin,
        Icon: Gift,
        accent: "#f0a500",
        preview: {
          type: "report",
          head: "Free Property Documents",
          badge: "Free",
          metric: "140+",
          unit: "",
          metricLabel: "Documents Available",
          status: "Free on sign-up",
          rows: [
            ["Sign up for free", "1 min", "good"],
            ["Download any document", "Included", "good"],
            ["Instant access", "Seconds", "accent"],
            ["No credit card required", "Free", "good"],
          ],
        },
      };

  // Order depends on who's looking:
  //  · Logged OUT → creditsSlide leads. Free documents on sign-up is the
  //    strongest hook for a first-time visitor, so it opens the carousel.
  //  · Logged IN  → it has already done its job and becomes "Refer & Earn",
  //    so the products lead and it goes LAST.
  // Whichever lands at index 0 is the LCP element; the eager/fetchpriority
  // treatment below keys off position, so it follows this order automatically.
  const slides = isLoggedIn
    ? [...PRODUCTS, creditsSlide]
    : [creditsSlide, ...PRODUCTS];

  // ── Carousel (infinite round-robin) ──
  // A clone of the first slide is appended after the last one (`loopSlides`).
  // Autoplay advances the index forward WITHOUT wrapping; when it lands on the
  // clone (idx === total) the track finishes animating forward onto it, then we
  // snap instantly (transition disabled) back to the real first slide. Because
  // the clone is pixel-identical to slide 0, the snap is invisible — so the
  // carousel loops forever forward and never visibly "rewinds" back to slide 1.
  const [idx, setIdx] = useState(0);
  // Off-screen slides are lazy, so each one's art used to start downloading
  // only as it slid in (a blank card for a moment). Warm them all shortly
  // after load instead, without competing with the first paint.
  useEffect(() => {
    const t = setTimeout(() => {
      PRODUCTS.forEach((p) => {
        if (p.image) new Image().src = p.image;
      });
    }, 1500);
    return () => clearTimeout(t);
  }, []);
  const [paused, setPaused] = useState(false);
  const [noAnim, setNoAnim] = useState(false); // disable the transition for the snap
  // ── Swipe / drag state ──
  const [dragPx, setDragPx] = useState(null); // live horizontal drag offset in px; null when not dragging
  const dragRef = useRef({ active: false, startX: 0, startY: 0, axis: null, width: 1 });
  const swipedRef = useRef(false); // true right after a drag, so the card's click is suppressed
  const total = slides.length;
  const loopSlides = [...slides, slides[0]]; // real slides + clone of the first
  const activeIdx = idx % total; // the real slide on screen (for dots/aria)

  const go = (n) => {
    setNoAnim(false);
    setIdx(((n % total) + total) % total);
  };
  // NEVER advance past the clone. `idx > total` would translate the track beyond
  // the last rendered slide (loopSlides is only total+1 long) — an EMPTY frame —
  // and the snap effect below only fires at exactly `total`, so it would stay
  // stuck there. Clicking next while the clone is showing (i.e. faster than the
  // 580ms snap) instead jumps invisibly to the real slide 0, then animates to 1.
  // Advance/retreat one slide. Driven by swipe/drag now that the visible
  // prev/next arrows were removed — same infinite-loop snap logic they used.
  const goNext = () => {
    if (idx < total) {
      setIdx((i) => i + 1); // may land on the clone; the effect below snaps to 0
      return;
    }
    setNoAnim(true);
    setIdx(0);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        setNoAnim(false);
        setIdx(1);
      }),
    );
  };

  const goPrev = () => {
    if (idx > 0) {
      setIdx((i) => i - 1);
      return;
    }
    // At the first slide: snap invisibly onto the clone (looks like slide 0),
    // then animate to the last real slide so "previous" also loops smoothly.
    setNoAnim(true);
    setIdx(total);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        setNoAnim(false);
        setIdx(total - 1);
      }),
    );
  };

  useEffect(() => {
    if (paused) return;
    // Clamped at the clone for the same reason as goNext — the snap effect resets
    // it to 0 from there, so autoplay can never translate onto an empty frame.
    const id = setInterval(() => setIdx((i) => Math.min(i + 1, total)), 5000);
    return () => clearInterval(id);
  }, [paused, total]);

  // Touch/hover pauses autoplay and holds it paused for a few seconds after
  // the pointer leaves, instead of resuming the instant the cursor lifts —
  // so a quick tap or a mouse passing over the card doesn't feel like it
  // barely registered.
  const resumeTimerRef = useRef(null);
  const RESUME_DELAY = 7000; // 5–10s hold window
  const holdPaused = useCallback(() => {
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    setPaused(true);
  }, []);
  const scheduleResume = useCallback(() => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      resumeTimerRef.current = null;
      setPaused(false);
    }, RESUME_DELAY);
  }, []);
  useEffect(() => {
    return () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, []);

  // ── Pointer swipe/drag (one code path for touch + mouse) ──
  // touch-action:pan-y (CSS) keeps vertical page scrolling native, while we take
  // over horizontal gestures: the track follows the finger/cursor, then snaps to
  // the next or previous slide on release once the drag passes ~18% of the width.
  const onDragStart = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return; // left button only
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      axis: null,
      width: e.currentTarget.offsetWidth || 1,
    };
    swipedRef.current = false;
    holdPaused(); // pause autoplay while the user is interacting
  };
  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    // Decide the gesture axis once, after a small deadzone.
    if (!d.axis) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      d.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (d.axis === "x") {
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* pointer capture is best-effort */
        }
      }
    }
    if (d.axis !== "x") return; // vertical intent → let the page scroll
    swipedRef.current = true;
    setDragPx(dx);
  };
  const onDragEnd = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    d.active = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (d.axis === "x") {
      const dx = e.clientX - d.startX;
      const threshold = Math.min(d.width * 0.18, 90);
      setDragPx(null); // clear the live offset; the idx change animates to the slide
      if (dx <= -threshold) goNext();
      else if (dx >= threshold) goPrev();
    }
    d.axis = null;
    scheduleResume(); // resume autoplay after the usual hold window
  };

  // Landed on the appended clone → let the forward animation finish, then snap
  // (no transition) back to the real first slide.
  useEffect(() => {
    if (idx !== total) return;
    const t = setTimeout(() => {
      setNoAnim(true);
      setIdx(0);
    }, 580); // just after the 0.55s track transition
    return () => clearTimeout(t);
  }, [idx, total]);

  // Once an instant (no-anim) jump has painted, re-enable the transition so the
  // next move animates normally.
  useEffect(() => {
    if (!noAnim) return;
    const r = requestAnimationFrame(() =>
      requestAnimationFrame(() => setNoAnim(false)),
    );
    return () => cancelAnimationFrame(r);
  }, [noAnim, idx]);

  // ── Document search (same behaviour as the "Explore Land Documents" page) ──
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const stateRef = useRef(null); // hero "Select state" dropdown
  const docStateRef = useRef(null); // "Explore Documents in <state>" dropdown

  const docSuggestions = useMemo(() => {
    if (!selectedState || !query.trim() || query.trim().length < 2) return [];
    const term = query.toLowerCase().replace(/\s+/g, "");
    const results = [];
    // Only the currently-selected state's documents, not other states.
    ["non-agriculture", "agriculture"].forEach((mode) => {
      if (Array.isArray(selectedState[mode])) {
        selectedState[mode].forEach((doc) => {
          const id = doc.name.toLowerCase().replace(/\s+/g, "");
          if (id.includes(term)) {
            results.push({
              ...doc,
              state: selectedState.id,
              stateName: selectedState.name,
              mode,
            });
          }
        });
      }
    });
    return results;
  }, [selectedState, query]);

  // Open the suggestions panel whenever there are matches.
  useEffect(() => {
    setActiveIndex(-1);
    setSearchOpen(docSuggestions.length > 0);
  }, [query, docSuggestions.length]);

  // Rotating placeholder, cycles through the selected state's document names
  // (e.g. "Search for 'Sale Deed'") every 3s so the search bar hints at what's
  // available instead of only ever showing "Certified Copy".
  const placeholderDocs = useMemo(() => {
    const stateName = selectedState?.name?.trim();
    // Doc names come back prefixed with the state name (e.g. "Andhra Pradesh
    // Mutation(Khata) Certificate") — strip it so the placeholder shows just
    // the document, since the state is already shown in the picker beside it.
    const stripStateName = (name) => {
      if (!stateName) return name;
      const prefix = new RegExp(`^${stateName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+`, "i");
      return name.replace(prefix, "");
    };
    const names = [
      ...(selectedState?.["non-agriculture"] || []),
      ...(selectedState?.agriculture || []),
    ]
      .map((doc) => doc.name)
      .filter(Boolean)
      .map(stripStateName);
    return names.length > 0 ? [...new Set(names)] : ["Certified Copy"];
  }, [selectedState]);

  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  useEffect(() => {
    setPlaceholderIndex(0);
  }, [placeholderDocs]);
  useEffect(() => {
    if (placeholderDocs.length < 2) return;
    const id = setInterval(() => {
      setPlaceholderIndex((i) => (i + 1) % placeholderDocs.length);
    }, 3000);
    return () => clearInterval(id);
  }, [placeholderDocs]);

  // Close the suggestions panel + either state dropdown on an outside click.
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
      if (stateRef.current && !stateRef.current.contains(e.target)) {
        setStateOpen(false);
      }
      if (docStateRef.current && !docStateRef.current.contains(e.target)) {
        setDocStateOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelectDoc = useCallback(
    (doc) => {
      setQuery("");
      setSearchOpen(false);
      navigate(`/${doc.state}/${doc.id}`, {
        state: {
          category:
            doc.mode === "agriculture"
              ? { name: "Agriculture", id: "agriculture" }
              : { name: "Non-Agriculture", id: "non-agriculture" },
          stateId: doc.state,
          documentId: doc.id,
        },
      });
      window.scrollTo(0, 0);
    },
    [navigate],
  );

  const goSearch = () => {
    // Enter / Search click with a matching document jumps straight to it.
    if (docSuggestions.length > 0) {
      handleSelectDoc(docSuggestions[0]);
      return;
    }
    // No document match → open the SELECTED state's documents (e.g. /telangana),
    // the same target as a state card on the States page — NOT the all-states
    // documents list. Only falls back to /states when no state is selected.
    navigate(selectedState?.id ? `/${selectedState.id}` : "/states");
    window.scrollTo(0, 0);
  };

  const handleSearchKeyDown = (e) => {
    if (!searchOpen) {
      if (e.key === "Enter") goSearch();
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, docSuggestions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, -1));
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < docSuggestions.length) {
          handleSelectDoc(docSuggestions[activeIndex]);
        } else {
          goSearch();
        }
        break;
      case "Escape":
        setSearchOpen(false);
        break;
      default:
        break;
    }
  };

  const openProduct = (p) => {
    if (p.action) p.action();
    else if (p.href) window.open(p.href, "_blank", "noopener");
    else {
      navigate(p.to);
      window.scrollTo(0, 0);
    }
  };

  return (
    <>
      <section className="psh-section">
        <div className="psh-wrap">
          {/* ── Product carousel ── */}
          {/* Visible prev/next arrows were removed. Instead, invisible edge
              click-zones (psh-hit) below take a click on the left/right edge to
              the previous/next slide, while the centre — image, text, CTA —
              opens the product. Slides can also be swiped/dragged. */}
          <div className="psh-carousel-wrap">
            <button
              type="button"
              className="psh-hit psh-hit--prev"
              aria-label="Previous slide"
              onClick={goPrev}
            >
              <span className="psh-hit-arrow" aria-hidden="true">&lt;</span>
            </button>
            <button
              type="button"
              className="psh-hit psh-hit--next"
              aria-label="Next slide"
              onClick={goNext}
            >
              <span className="psh-hit-arrow" aria-hidden="true">&gt;</span>
            </button>
            <div
              className="psh-carousel"
              onMouseEnter={holdPaused}
              onMouseLeave={scheduleResume}
              onPointerDown={onDragStart}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              onPointerCancel={onDragEnd}
            >

              {/* Sliding track, every slide is one viewport wide, so the row
                  slides horizontally (not fade-swap) and all cards are the same
                  size. */}
              <div className="psh-viewport">
                <div
                  className="psh-track"
                  style={{
                    // While dragging, add the live pixel offset so the track
                    // follows the finger/cursor; otherwise rest on the slide.
                    transform:
                      dragPx !== null
                        ? `translateX(calc(${-idx * 100}% + ${dragPx}px))`
                        : `translateX(${-idx * 100}%)`,
                    // No transition while dragging (follow the finger) or during
                    // the invisible clone→first snap.
                    transition: noAnim || dragPx !== null ? "none" : undefined,
                  }}
                >
                  {loopSlides.map((s, i) => {
                    // Slide 0 is on screen at first paint, so its image is the
                    // LCP element — it must load eagerly at high priority. Every
                    // other slide (including the appended clone) stays lazy.
                    const isLcp = i === 0;
                    return (
                      <div className="psh-slide" key={i}>
                        <div
                          className="psh-pcard"
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            // A swipe just happened → treat as a drag, not a tap.
                            if (swipedRef.current) {
                              swipedRef.current = false;
                              return;
                            }
                            openProduct(s);
                          }}
                          onKeyDown={(e) =>
                            (e.key === "Enter" || e.key === " ") &&
                            openProduct(s)
                          }
                        >
                          {/* Branded promo image on the left of each slide.
                              Falls back to the live mock preview if no image. */}
                          <div className="psh-pcard-media" aria-hidden="true">
                            {s.image ? (
                              <img
                                src={s.image}
                                alt={s.imageAlt || s.name}
                                // credits.webp has a lot of transparent margin
                                // baked into its 800x532 canvas (unlike the
                                // other slide art, which fills it edge to
                                // edge), so under object-fit:contain the
                                // illustration renders visibly smaller than
                                // the other cards. object-fit:cover zooms
                                // past that margin so it matches their scale.
                                className={`psh-pcard-media-img${
                                  s.id === "credits"
                                    ? " psh-pcard-media-img--fill"
                                    : ""
                                }`}
                                // Every slide art is 800x532. Declaring the
                                // intrinsic size lets the browser reserve the
                                // box before the file arrives, so the card no
                                // longer collapses-then-pushes on load (CLS).
                                width={800}
                                height={532}
                                loading={isLcp ? "eager" : "lazy"}
                                // Lowercase on purpose. react-dom 18.3.1 has no
                                // knowledge of `fetchPriority`, so the camelCase
                                // spelling the lint rule wants (React 19's) would
                                // be dropped as an unrecognized prop and warn in
                                // the console; lowercase passes straight through
                                // as a real attribute. Revisit on React 19.
                                // eslint-disable-next-line react/no-unknown-property
                                fetchpriority={isLcp ? "high" : undefined}
                                decoding={isLcp ? "sync" : "async"}
                              />
                            ) : (
                              <Preview p={s} />
                            )}
                          </div>

                          <div className="psh-pcard-content">
                            <h2 className="psh-pcard-title">{s.title}</h2>
                            {s.desc && <p className="psh-pcard-desc">{s.desc}</p>}
                            <div className="psh-pcard-chips">
                              {s.chips.map((c) => (
                                <span
                                  key={c}
                                  className="psh-pcard-chip"
                                  style={{ borderColor: `${s.accent}66` }}
                                >
                                  {c}
                                </span>
                              ))}
                            </div>
                            <div className="psh-pcard-actions">
                              <button
                                type="button"
                                className="psh-pcard-cta"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openProduct(s);
                                }}
                              >
                                {s.cta}
                                <ArrowRight size={16} />
                              </button>
                              {s.sample && (
                                <button
                                  type="button"
                                  className="psh-pcard-cta2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSampleKind(s.sample);
                                    setShowSample(true);
                                  }}
                                >
                                  View Sample
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </div>

          {/* ── Title + search bar ── */}
          <h1 className="psh-title">
            India&apos;s Most Trusted Platform for{" "}
            <span className="psh-title-accent">Every Property Decision</span>
          </h1>

          <div className="psh-searchhost">
            <div className="psh-searchbar" ref={searchRef}>
              <div className="psh-state" ref={stateRef}>
                {/* Small "Start here" tag above the state pill — mobile only. */}
                <span className="psh-start-tag" aria-hidden="true">Start here</span>
                <button
                  type="button"
                  className="psh-state-btn"
                  onClick={() => setStateOpen((o) => !o)}
                >
                  <MapPin size={16} className="psh-state-pin" />
                  <span>{selectedState?.name || "Select state"}</span>
                  <ChevronDown size={15} />
                </button>
                {stateOpen && (
                  <div className="psh-state-menu">
                    <div className="psh-state-menu-head">
                      <span>Select state</span>
                      <button
                        type="button"
                        className="psh-state-close"
                        onClick={() => setStateOpen(false)}
                        aria-label="Close state list"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    {states.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="psh-state-item"
                        onClick={() => {
                          setSelectedState(s);
                          setStateOpen(false);
                        }}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="psh-input-wrap">
                <button
                  type="button"
                  className="psh-input-icon-btn"
                  onClick={goSearch}
                  aria-label="Search"
                >
                  <Search size={18} className="psh-input-icon" />
                </button>
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() =>
                    docSuggestions.length > 0 && setSearchOpen(true)
                  }
                  placeholder={`Search for '${placeholderDocs[placeholderIndex]}'`}
                  autoComplete="off"
                  className="psh-input"
                />
              </div>

              <button type="button" className="psh-search-btn" onClick={goSearch}>
                Search
              </button>

              {/* Live document suggestions, matches the documents-page search */}
              {searchOpen && docSuggestions.length > 0 && (
                <div className="psh-suggest" role="listbox">
                  <div className="psh-suggest-head">Documents</div>
                  {docSuggestions.map((doc, i) => (
                    <button
                      key={`${doc.state}-${doc.id}-${doc.mode}`}
                      type="button"
                      role="option"
                      aria-selected={i === activeIndex}
                      className={`psh-suggest-item${
                        i === activeIndex ? " is-active" : ""
                      }`}
                      onMouseEnter={() => setActiveIndex(i)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectDoc(doc);
                      }}
                    >
                      <span className="psh-suggest-name">
                        <HighlightText text={doc.name} query={query} />
                      </span>
                      <span className="psh-suggest-meta">
                        {doc.mode === "agriculture"
                          ? "Agriculture"
                          : "Non-Agriculture"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Trust line ── */}
          <div className="psh-trust">
            {TRUST.map(({ Icon: TIcon, value, label }) => (
              <span key={label} className="psh-trust-item">
                <TIcon size={16} className="psh-trust-icon" />
                <span className="psh-trust-text">
                  {value} {label.toLowerCase()}
                </span>
                <span className="psh-trust-value">{value}</span>
                <span className="psh-trust-label">{label}</span>
              </span>
            ))}
          </div>
        </div>
        {/* "Explore documents" scroll cue. Hidden unless a page opts in (the
            desktop home's .home-light, where the hero fills the first screen). */}
        <button
          type="button"
          className="psh-scrollcue"
          onClick={() => {
            const el = document.querySelector(".psh-docsec");
            if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 64, behavior: "smooth" });
          }}
        >
          Explore documents
          <ChevronDown size={18} aria-hidden="true" />
        </button>
      </section>

      {/* ── State documents (exactly like the property-documents page) ── */}
      {selectedState && (
        <section className="psh-docsec bg-[#f2f6fb] pb-12 pt-12">
          <div className="mx-auto w-full max-w-screen-xl px-4 sm:px-6">
            <div className="relative mb-8 text-center max-md:mb-5">
              <h2 className="inline-flex items-center gap-1 whitespace-nowrap text-base font-bold text-primary sm:gap-2 sm:text-2xl md:text-[28px]">
                <span className="whitespace-nowrap">Explore Documents in</span>
                <span className="relative inline-block" ref={docStateRef}>
                  {/* Animated "select your state" hint. Mobile: text above an
                      arrow. Web: text after the state name with its own arrow
                      arching back onto the name (see CSS). */}
                  <span className="psh-state-hint psh-state-hint--docs" aria-hidden="true">
                    <span className="psh-state-hint-text">Select your<br />state here!</span>
                    <svg className="psh-state-hint-arrow" viewBox="0 0 88 46" fill="none">
                      <path d="M8 8 C 34 4, 60 12, 74 36" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
                      <path d="M74 36 L 62 33 M74 36 L 71 24" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
                    </svg>
                    <svg className="psh-state-hint-arrow-web" viewBox="0 0 72 34" fill="none">
                      <path d="M66 31 C 62 6, 22 0, 10 24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      <path d="M10 24 L 18.3 18.5 M10 24 L 9.4 14" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <button
                    type="button"
                    onClick={() => setDocStateOpen((o) => !o)}
                    className="inline-flex items-center gap-1 border-b-2 border-gold pb-0.5 text-primary"
                  >
                    {selectedState.name}
                    <ChevronDown
                      className={`h-5 w-5 text-gold transition-transform ${
                        docStateOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {docStateOpen && (
                    <div className="absolute left-1/2 top-full z-40 mt-2 flex max-h-[340px] w-[260px] max-w-[92vw] -translate-x-1/2 flex-col gap-1 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 text-left shadow-[0_20px_50px_-12px_rgba(0,0,0,0.3)]">
                      <div className="sticky top-0 z-10 -mx-2 -mt-2 mb-1 flex items-center justify-between gap-2 rounded-t-xl border-b border-slate-100 bg-white px-3 py-2">
                        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                          Select state
                        </span>
                        <button
                          type="button"
                          onClick={() => setDocStateOpen(false)}
                          aria-label="Close state list"
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-primary"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {states.map((s) => {
                        const on =
                          String(s.id) === String(selectedState.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => {
                              setSelectedState(s);
                              setDocStateOpen(false);
                            }}
                            className={`block w-full shrink-0 whitespace-nowrap px-4 py-2.5 text-left text-[15px] transition hover:bg-slate-50 ${
                              on
                                ? "font-bold text-primary"
                                : "font-medium text-slate-600"
                            }`}
                          >
                            {s.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </span>
              </h2>
            </div>

            {/* Category tabs */}
            <div className="mx-auto mb-9 flex max-w-md max-md:mb-5 items-center gap-2 rounded-full bg-white p-1 shadow-sm ring-1 ring-slate-200">
              <button
                type="button"
                disabled={!hasNon}
                onClick={() => setActiveCategory("non-agriculture")}
                className={`flex-1 rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                  activeCategory === "non-agriculture"
                    ? "bg-primary text-white shadow"
                    : "text-slate-600 hover:text-primary disabled:opacity-40"
                }`}
              >
                Non-Agriculture
              </button>
              <button
                type="button"
                disabled={!hasAgri}
                onClick={() => setActiveCategory("agriculture")}
                className={`flex-1 rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                  activeCategory === "agriculture"
                    ? "bg-primary text-white shadow"
                    : "text-slate-600 hover:text-primary disabled:opacity-40"
                }`}
              >
                Agriculture
              </button>
            </div>

            {/* Pin-the-property entry, between the category toggle and the
                document grid — the same slot it holds on /states and on a
                state page. */}
            <div className="mx-auto mb-8 mt-6 w-full max-w-2xl">
              <MapRequestBanner onOpen={() => setMapRequestOpen(true)} />
            </div>

            {/* Document cards */}
            {docs.length === 0 ? (
              <p className="rounded-2xl border border-slate-200 bg-white px-4 py-12 text-center text-grey shadow-sm">
                No documents in this category yet.
              </p>
            ) : (
              <div className="grid grid-cols-2 items-stretch gap-3 md:gap-7 lg:grid-cols-[repeat(auto-fill,minmax(300px,1fr))] [&>*]:h-full">
                {docs.map((document) => (
                  <PropertyDocumentCard
                    key={document.id}
                    document={document}
                    stateId={stateId}
                    category={{
                      name:
                        activeCategory === "non-agriculture"
                          ? "Non-Agriculture"
                          : "Agriculture",
                      id: activeCategory,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <style>{`
        .psh-section {
          background: linear-gradient(180deg, #0a1f3d 0%, #102a4e 46%, #153568 100%);
          padding: 0 0 0;
          /* Own stacking context above the "Explore Documents" section below,
             so the open state dropdown isn't clipped by it. */
          position: relative;
          z-index: 2;
        }
        /* Premium atmosphere, soft gold + sky glows behind the content, echoing
           the home hero so the slider reads as part of the same system. */
        .psh-section::before {
          content: "";
          position: absolute;
          inset: 0;
          background:
            radial-gradient(1100px 440px at 50% -14%, rgba(255,169,0,0.13), transparent 60%),
            radial-gradient(760px 480px at 90% 6%, rgba(94,169,255,0.10), transparent 62%),
            radial-gradient(680px 460px at 4% 96%, rgba(94,169,255,0.06), transparent 60%);
          pointer-events: none;
          z-index: 0;
        }
        /* Faint engineering grid, masked to fade out, same detail as the hero. */
        .psh-section::after {
          content: "";
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px);
          background-size: 54px 54px;
          -webkit-mask-image: radial-gradient(ellipse 82% 72% at 50% 0%, #000 0%, transparent 72%);
          mask-image: radial-gradient(ellipse 82% 72% at 50% 0%, #000 0%, transparent 72%);
          pointer-events: none;
          z-index: 0;
        }
        .psh-scrollcue { display: none; }
        .psh-wrap {
          max-width: 1200px;
          margin: 0 auto;
          padding: 30px 20px 46px;
          position: relative;
          z-index: 1;
        }

        /* Collapses on scroll */
        .psh-collapse {
          overflow: hidden;
          transition: max-height 0.45s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease, margin-bottom 0.4s ease;
        }

        /* ── Product carousel ── */
        /* Full-bleed: the slide area spans the full viewport width (edge to edge)
           so the arrows sit at the screen edges. The inner content is capped and
           centred (see .psh-pcard) so the image + text keep their original
           placement. Safe because .jaaga-home sets overflow-x: clip — no side
           scrollbar. */
        .psh-carousel-wrap {
          position: relative;
          width: 100vw;
          margin-left: calc(50% - 50vw);
          margin-right: calc(50% - 50vw);
        }
        .psh-carousel {
          position: relative;
          touch-action: pan-y;      /* keep vertical page scroll native, take horizontal */
          cursor: grab;
          user-select: none;
          -webkit-user-select: none;
        }
        .psh-carousel:active { cursor: grabbing; }
        /* Stop the browser's native image drag from hijacking a swipe. */
        .psh-carousel img { -webkit-user-drag: none; user-drag: none; }
        /* Left/right click-zones, each showing a translucent arrow pinned to the
           edge. Click the edge → prev/next slide; the centre (image, text, CTA)
           still opens the product. */
        .psh-hit {
          position: absolute; top: 0; bottom: 0;
          width: clamp(56px, 7%, 88px);
          z-index: 8;
          display: grid; place-items: center;
          background: transparent; border: none; padding: 0; margin: 0;
          cursor: pointer;
        }
        .psh-hit--prev { left: 0; justify-items: start; padding-left: 10px; }
        .psh-hit--next { right: 0; justify-items: end; padding-right: 10px; }
        /* Bare translucent arrow — no circle/background, just the glyph. */
        .psh-hit-arrow {
          color: rgba(255,255,255,0.55);
          font-size: 30px; font-weight: 700; line-height: 1;
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .psh-hit:hover .psh-hit-arrow {
          color: #ffffff;
          transform: scale(1.12);
        }
        .psh-hit:focus-visible { outline: none; }
        .psh-hit:focus-visible .psh-hit-arrow {
          outline: 2px solid #f0a500; outline-offset: 2px;
        }
        /* Borderless viewport: clips the sliding track but has no card frame —
           the moving content sits directly on the section background, the same
           way on web and mobile. */
        .psh-viewport {
          position: relative;
          overflow: hidden;
        }
        .psh-track {
          display: flex;
          align-items: stretch;
          transition: transform 0.55s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
        }
        /* Each slide is exactly one viewport wide → all 4 cards are identical in
           width; align-items:stretch makes them share the tallest height. */
        .psh-slide { flex: 0 0 100%; min-width: 100%; display: flex; }
        .psh-pcard {
          width: 100%;
          /* Slide area is full-bleed, but the content stays capped + centred so
             the image and text keep the same placement as the boxed layout. */
          max-width: 1160px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 34px;
          padding: 24px 44px 40px;
          min-height: 260px;
          cursor: pointer;
        }
        .psh-pcard-content {
          flex: 1 1 53%; min-width: 0;
          display: flex; flex-direction: column; justify-content: center;
        }
        /* Left-side branded promo image */
        .psh-pcard-media {
          position: relative;
          flex: 0 0 47%; min-width: 0;
          display: flex; justify-content: center; align-items: center;
          padding: 6px;
        }
        /* Soft halo behind the promo image for depth. */
        .psh-pcard-media::before {
          content: "";
          position: absolute; inset: 14% 9%;
          background: radial-gradient(58% 58% at 50% 46%, rgba(255,255,255,0.18), transparent 70%);
          filter: blur(24px);
          z-index: 0;
        }
        .psh-pcard-media-img {
          position: relative; z-index: 1;
          max-width: 100%; max-height: 290px; width: auto; height: auto;
          /* Matches the 800x532 width/height attributes on the tag: pins the
             reserved box to the final rendered box, so nothing reflows once
             the image decodes. */
          aspect-ratio: 800 / 532;
          object-fit: contain;
          border-radius: 14px;
          border: none;
          filter: drop-shadow(0 22px 34px rgba(0,0,0,0.32));
          display: block;
          transition: transform 0.42s cubic-bezier(0.22,1,0.36,1), filter 0.42s ease;
        }
        /* Zooms past credits.webp's baked-in transparent margin so its
           illustration reads at the same scale as the other slide art, and
           runs a bit larger than the shared max-height on top of that. */
        .psh-pcard-media-img--fill {
          object-fit: cover;
          max-height: 330px;
        }
        .psh-pcard:hover .psh-pcard-media-img {
          transform: translateY(-4px) scale(1.02);
          filter: drop-shadow(0 30px 44px rgba(0,0,0,0.4));
        }
        /* Fallback live mock preview (only if a slide has no image) */
        .psh-pcard-media .psh-pv {
          transform: rotate(1.5deg);
          transition: transform 0.3s ease;
        }
        .psh-pcard:hover .psh-pcard-media .psh-pv { transform: rotate(0deg) translateY(-2px); }
        .psh-pcard-eyebrow {
          display: flex; align-items: center; gap: 8px; margin-bottom: 11px;
          flex-wrap: wrap;
        }
        .psh-pcard-badge {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 10px; font-weight: 800; letter-spacing: 0.05em;
          text-transform: uppercase;
          padding: 4px 11px 4px 9px; border-radius: 999px;
          border: 1px solid transparent;
          white-space: nowrap;
        }
        .psh-pcard-badge::before {
          content: ""; width: 5px; height: 5px; border-radius: 50%;
          background: currentColor; flex-shrink: 0;
          box-shadow: 0 0 0 3px rgba(255,255,255,0.4);
        }
        .psh-pcard-icon {
          width: 28px; height: 28px; border-radius: 8px;
          display: grid; place-items: center; flex-shrink: 0;
        }
        .psh-pcard-name {
          font-size: 12px; font-weight: 800; letter-spacing: 0.12em;
          text-transform: uppercase;
        }
        .psh-pcard-soon {
          font-size: 9px; font-weight: 800; letter-spacing: 0.08em;
          text-transform: uppercase; color: #14213d;
          background: linear-gradient(135deg,#ffc629,#f0a500);
          border-radius: 999px; padding: 3px 9px;
        }
        .psh-pcard-title {
          font-size: clamp(21px, 2.5vw, 30px); font-weight: 900; color: #ffffff;
          line-height: 1.1; letter-spacing: -0.5px; margin: 0 0 9px;
        }
        .psh-pcard-desc {
          font-size: 13.5px; color: rgba(255,255,255,0.82); line-height: 1.55;
          max-width: 460px; margin: 0 0 12px;
        }
        .psh-pcard-chips {
          display: flex; flex-wrap: wrap; gap: 8px;
        }
        .psh-pcard-chip {
          display: inline-flex; align-items: center; gap: 7px;
          font-size: 11.5px; font-weight: 600; color: #ffffff;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.22);
          border-radius: 999px; padding: 5px 12px 5px 10px;
          box-shadow: none;
        }
        .psh-pcard-chip::before {
          content: ""; width: 5px; height: 5px; border-radius: 1.5px;
          background: #f0a500; transform: rotate(45deg); flex-shrink: 0;
        }
        /* Inline CTA row, directly under the copy */
        .psh-pcard-actions {
          display: flex; align-items: center; gap: 10px;
          margin-top: 14px; flex-wrap: wrap;
        }
        .psh-pcard-cta {
          display: inline-flex; align-items: center; gap: 8px;
          background: linear-gradient(135deg,#ffc629,#f0a500);
          color: #14213d; border: none; border-radius: 12px;
          padding: 11px 22px; cursor: pointer;
          font-size: 13.5px; font-weight: 800; letter-spacing: 0.01em;
          box-shadow: 0 12px 26px -10px rgba(240,165,0,0.6), inset 0 1px 0 rgba(255,255,255,0.45);
          transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
        }
        .psh-pcard-cta:hover {
          transform: translateY(-2px);
          filter: brightness(1.03);
          box-shadow: 0 18px 38px -10px rgba(240,165,0,0.72), inset 0 1px 0 rgba(255,255,255,0.5);
        }
        .psh-pcard-cta2 {
          display: inline-flex; align-items: center;
          background: #ffffff;
          color: #153568; border: 1.5px solid rgba(21,53,104,0.28);
          border-radius: 11px; padding: 9px 18px; cursor: pointer;
          font-size: 13px; font-weight: 700;
          transition: background 0.18s ease, border-color 0.18s ease;
        }
        .psh-pcard-cta2:hover {
          background: #f1f5fb;
          border-color: #153568;
        }
        .psh-pcard-cta:focus-visible, .psh-pcard-cta2:focus-visible {
          outline: 2px solid #153568; outline-offset: 3px;
        }

        /* Right-side product image */
        .psh-pcard-preview {
          flex: 1 1 46%; min-width: 0;
          display: flex; justify-content: center; align-items: center;
        }
        .psh-pcard-img {
          width: 100%;
          height: 240px;
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.16);
          box-shadow: 0 26px 56px -22px rgba(0,0,0,0.7);
          object-fit: cover;
          object-position: top center;
          background: #fff;
          display: block;
        }
        .psh-pv {
          width: 100%; max-width: 320px;
          background: #ffffff; border-radius: 14px; padding: 13px 15px;
          box-shadow: 0 18px 44px -24px rgba(21,53,104,0.5);
          color: #0f1b2d;
        }
        .psh-pv-head { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
        .psh-pv-head-icon {
          width: 24px; height: 24px; border-radius: 7px;
          display: grid; place-items: center; flex-shrink: 0;
        }
        .psh-pv-head-title {
          flex: 1; font-size: 11.5px; font-weight: 800; color: #153568;
          text-transform: uppercase; letter-spacing: 0.04em;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .psh-pv-badge {
          font-size: 8.5px; font-weight: 800; letter-spacing: 0.06em;
          text-transform: uppercase; color: #64748b;
          background: #f1f5f9; border-radius: 999px; padding: 3px 8px;
          flex-shrink: 0;
        }
        .psh-pv-top {
          display: flex; align-items: center; gap: 12px;
          background: #f0fdf4; border: 1px solid #bbf7d0;
          border-radius: 12px; padding: 9px 12px; margin-bottom: 12px;
        }
        .psh-pv-metric { display: flex; align-items: baseline; gap: 2px; }
        .psh-pv-metric-num { font-size: 28px; font-weight: 900; line-height: 1; }
        .psh-pv-metric-unit { font-size: 13px; font-weight: 700; color: #64748b; }
        .psh-pv-metric-side { display: flex; flex-direction: column; gap: 4px; }
        .psh-pv-metric-label {
          font-size: 9.5px; font-weight: 700; color: #64748b;
          text-transform: uppercase; letter-spacing: 0.06em;
        }
        .psh-pv-status {
          font-size: 10px; font-weight: 800; color: #15803d;
          background: #dcfce7; border-radius: 999px; padding: 2px 8px;
          width: fit-content;
        }
        .psh-pv-rows { display: flex; flex-direction: column; gap: 7px; }
        .psh-pv-row {
          display: flex; align-items: center; justify-content: space-between;
          font-size: 12px; gap: 10px;
        }
        .psh-pv-row-left {
          display: flex; align-items: center; gap: 7px;
          color: #334155; font-weight: 500; min-width: 0;
        }
        .psh-pv-row-val { font-weight: 700; white-space: nowrap; flex-shrink: 0; }
        .psh-pv-row-val--good { color: #15803d; }
        .psh-pv-row-val--warn { color: #d97706; }
        .psh-pv-row-val--accent { color: #2563eb; }
        .psh-pv-row-val--muted { color: #64748b; }

        /* Map preview */
        .psh-pv--map { padding: 0; overflow: hidden; }
        .psh-pv-map-canvas {
          position: relative; height: 132px;
          background:
            repeating-linear-gradient(0deg, rgba(0,0,0,0.05) 0 1px, transparent 1px 22px),
            repeating-linear-gradient(90deg, rgba(0,0,0,0.05) 0 1px, transparent 1px 22px),
            linear-gradient(135deg, #c9ddc2 0%, #aecaa4 55%, #93b98c 100%);
        }
        .psh-pv-map-plot {
          position: absolute; top: 32%; left: 38%;
          width: 92px; height: 64px;
          background: rgba(245,166,35,0.28);
          border: 2px solid #f0a500; border-radius: 6px;
          box-shadow: 0 0 18px rgba(240,165,0,0.5);
        }
        .psh-pv-map-pin {
          position: absolute; top: 40%; left: 52%;
          transform: translate(-50%, -50%);
          filter: drop-shadow(0 4px 6px rgba(0,0,0,0.4));
        }
        .psh-pv-map-tag {
          position: absolute;
          font-size: 9px; font-weight: 800; color: #fff;
          background: #153568; border-radius: 5px; padding: 1px 5px;
        }
        .psh-pv-map-info { padding: 11px 14px 13px; }
        .psh-pv-map-survey {
          font-size: 16px; font-weight: 900; color: #153568; line-height: 1.2;
        }
        .psh-pv-map-hash { color: #f0a500; }
        .psh-pv-map-loc { font-size: 11px; color: #5b6b86; margin-top: 3px; }
        .psh-pv-map-area { font-size: 11px; color: #5b6b86; margin-top: 2px; }
        .psh-pv-map-loc b, .psh-pv-map-area b { color: #153568; }

        .psh-nav {
          position: absolute; top: 50%; transform: translateY(-50%);
          z-index: 7;
          width: 42px; height: 42px;
          display: grid; place-items: center;
          background: transparent;
          border: none;
          box-shadow: none;
          cursor: pointer; color: #ffffff; padding: 0;
          transition: transform 0.2s ease;
        }
        .psh-nav:hover, .psh-nav:active, .psh-nav:focus-visible {
          color: #ffffff;
          transform: translateY(-50%) scale(1.1);
        }
        .psh-nav-symbol {
          font-size: 24px;
          line-height: 1;
          font-weight: 700;
        }
        .psh-nav--prev { left: 12px; }
        .psh-nav--next { right: 12px; }

        /* Title, white pill that overlaps the bottom of the carousel card */
        .psh-title {
          background: #ffffff;
          border-radius: 16px;
          padding: 20px 28px;
          text-align: center;
          font-size: clamp(18px, 2.4vw, 27px);
          font-weight: 800;
          color: #0f1b2d;
          box-shadow: 0 22px 50px -22px rgba(0,0,0,0.5);
          margin: -30px auto 0;
          /* Keep the whole headline on a single line on desktop/tablet; the
             card hugs the text width. Phones (≤768px) re-enable wrapping
             below so the long string doesn't overflow a narrow screen. */
          white-space: nowrap;
          width: max-content;
          max-width: min(96vw, 960px);
          position: relative;
          z-index: 6;
        }
        /* Inline (not block) so the accent stays on the same line as the rest. */
        .psh-title-accent { color: #f0a500; display: inline; }

        /* Search bar */
        .psh-searchhost { margin-top: 18px; }
        .psh-searchbar {
          display: flex;
          align-items: stretch;
          gap: 12px;
          background: #ffffff;
          border-radius: 16px;
          padding: 11px;
          min-height: 68px;
          max-width: 960px;
          margin: 0 auto;
          box-shadow: 0 18px 44px -22px rgba(0,0,0,0.45);
          position: relative;
          z-index: 40;
        }
        /* Pinned to the top of the screen once the user scrolls */
        .psh-searchhost--fixed {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 60;
          margin-top: 0;
          padding: 10px 16px;
          background: rgba(12, 34, 68, 0.97);
          backdrop-filter: blur(6px);
          box-shadow: 0 14px 34px -14px rgba(0, 0, 0, 0.6);
        }
        .psh-search-spacer { height: 78px; }
        .psh-state { position: relative; flex-shrink: 0; }
        .psh-state-btn {
          height: 100%;
          display: inline-flex; align-items: center; gap: 8px;
          padding: 0 20px;
          border: none; background: transparent; cursor: pointer;
          font-size: 16px; font-weight: 700; color: #153568;
          border-right: 1px solid #e5e7eb;
          white-space: nowrap;
        }
        .psh-state-pin { color: #f0a500; }
        /* Full states list, single column, scrolls if the list is long */
        .psh-state-menu {
          position: absolute; top: calc(100% + 8px); left: 0; z-index: 50;
          background: #fff; border: 1px solid #e5e7eb; border-radius: 14px;
          box-shadow: 0 22px 50px -16px rgba(0,0,0,0.32);
          padding: 8px;
          display: flex; flex-direction: column; gap: 2px;
          width: 260px; max-width: 90vw;
          max-height: 340px; overflow-y: auto;
        }
        .psh-state-item {
          flex-shrink: 0;
          display: block; width: 100%; text-align: left;
          padding: 10px 14px; border: none; background: transparent; cursor: pointer;
          font-size: 14.5px; color: #334155; border-radius: 9px;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .psh-state-item:hover { background: #f1f5f9; color: #153568; }
        /* Sticky header inside the menu, holds the close (X) so it stays reachable
           while the states list scrolls. Negative margins bleed over the menu's
           8px padding so the header spans the full width and sits flush at top. */
        .psh-state-menu-head {
          position: sticky; top: 0; z-index: 1;
          display: flex; align-items: center; justify-content: space-between; gap: 8px;
          margin: -8px -8px 6px; padding: 9px 12px;
          background: #fff; border-bottom: 1px solid #eef2f7;
          border-radius: 14px 14px 0 0;
          font-size: 11px; font-weight: 700; letter-spacing: 0.02em;
          text-transform: uppercase; color: #94a3b8;
        }
        .psh-state-close {
          display: inline-flex; align-items: center; justify-content: center;
          height: 24px; width: 24px; padding: 0;
          border: none; border-radius: 7px; cursor: pointer;
          background: #f1f5f9; color: #475569;
        }
        .psh-state-close:hover { background: #e2e8f0; color: #153568; }
        .psh-input-wrap {
          flex: 1; display: flex; align-items: center; gap: 8px;
          padding: 0 6px; min-width: 0;
        }
        .psh-input-icon-btn {
          display: inline-flex; align-items: center; justify-content: center;
          border: none; background: transparent; padding: 0; cursor: pointer;
          flex-shrink: 0;
        }
        .psh-input-icon { color: #94a3b8; flex-shrink: 0; }
        .psh-input {
          flex: 1; border: none; outline: none; background: transparent;
          font-size: 16px; color: #0f1b2d; min-width: 0;
        }
        .psh-search-btn {
          flex-shrink: 0;
          padding: 0 36px;
          border: none; border-radius: 12px; cursor: pointer;
          background: #153568; color: #fff;
          font-size: 16px; font-weight: 700;
          transition: background 0.2s ease;
        }
        .psh-search-btn:hover { background: #0e2750; }

        /* Document search suggestions, full-width panel under the search bar */
        .psh-suggest {
          position: absolute; top: calc(100% + 8px); left: 0; right: 0; z-index: 55;
          background: #fff; border: 1px solid #e5e7eb; border-radius: 16px;
          box-shadow: 0 22px 50px -16px rgba(0,0,0,0.32);
          padding: 6px; max-height: 360px; overflow-y: auto;
        }
        .psh-suggest-head {
          padding: 8px 14px 6px; font-size: 11px; font-weight: 800;
          letter-spacing: 0.08em; text-transform: uppercase; color: #94a3b8;
        }
        .psh-suggest-item {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          width: 100%; text-align: left; padding: 11px 14px; border: none;
          background: transparent; cursor: pointer; border-radius: 11px;
        }
        .psh-suggest-item:hover, .psh-suggest-item.is-active { background: #f1f5f9; }
        .psh-suggest-name {
          min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          font-size: 14.5px; font-weight: 600; color: #153568;
        }
        .psh-suggest-hl { background: #fef3c7; color: #b45309; border-radius: 3px; }
        .psh-suggest-meta {
          flex-shrink: 0; font-size: 11.5px; font-weight: 700; color: #f0a500;
          background: rgba(240,165,0,0.1); border-radius: 999px; padding: 3px 10px;
          white-space: nowrap;
        }

        /* Also explore */
        .psh-explore {
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
          justify-content: center;
          max-width: 920px; margin: 18px auto 0;
        }
        .psh-explore-label {
          color: #ffc874; font-size: 13.5px; font-weight: 700;
          letter-spacing: 0.01em;
        }
        .psh-explore-chip {
          display: inline-flex; align-items: center; gap: 7px;
          background: #ffffff; border: none; border-radius: 999px;
          padding: 8px 18px; cursor: pointer;
          font-size: 14px; font-weight: 700; color: #153568;
          box-shadow: 0 8px 20px -12px rgba(0,0,0,0.5);
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }
        .psh-explore-chip:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 26px -12px rgba(0,0,0,0.55);
        }
        .psh-explore-chip-icon { color: #f0a500; }

        /* Trust line */
        .psh-trust {
          display: flex; align-items: center; justify-content: center;
          gap: 28px; flex-wrap: wrap;
          max-width: 920px; margin: 16px auto 0;
        }
        .psh-trust-item {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 13.5px; font-weight: 600; color: #cfd9ea;
        }
        .psh-trust-icon { color: #f0a500; flex-shrink: 0; }
        .psh-trust-value, .psh-trust-label { display: none; }

        /* "Select your state" hint + "Start here" tag — shown on web AND mobile
           (same styling at all widths). */
        .psh-state-hint {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          position: absolute;
          left: 2px;
          bottom: calc(100% + 4px);
          z-index: 46;
          pointer-events: none;
          color: #f0a500;
          transform-origin: bottom left;
          animation: psh-hint-pulse 2.8s ease-in-out infinite;
        }
        .psh-state-hint-text {
          font-size: 11.5px;
          font-weight: 800;
          white-space: nowrap;
          text-shadow: 0 1px 2px rgba(0,0,0,0.45);
        }
        .psh-state-hint-arrow { width: 52px; height: 27px; margin-top: 1px; }
        .psh-state-hint--docs {
          left: auto;
          right: 0;
          /* Sit lower so the (taller) desktop hint stays on the white section and
             doesn't poke into the blue hero above. */
          bottom: calc(100% - 34px);
          align-items: flex-end;
          text-align: right;
          transform-origin: bottom right;
        }
        .psh-state-hint--docs .psh-state-hint-text {
          white-space: normal;
          line-height: 1.1;
          font-size: 15px;
          text-shadow: none;
          transform: translate(70px, 4px);
        }
        .psh-state-hint--docs .psh-state-hint-arrow { width: 60px; height: 30px; transform: scaleX(-1); }
        .psh-state-hint-arrow-web { display: none; }
        .psh-start-tag {
          display: inline-block;
          position: absolute;
          left: 6px;
          bottom: calc(100% + 6px);
          z-index: 46;
          pointer-events: none;
          background: #f0a500;
          color: #14213d;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.02em;
          padding: 3px 13px;
          border-radius: 999px;
          white-space: nowrap;
          box-shadow: 0 5px 12px -5px rgba(0,0,0,0.55);
          transform-origin: bottom left;
          animation: psh-hint-pulse 2.8s ease-in-out infinite;
        }
        .psh-start-tag::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 16px;
          border: 7px solid transparent;
          border-top-color: #f0a500;
        }
        /* Web only: the text sits AFTER the state name, vertically centred on
           it, and a curved arrow arches from the top of the text back over and
           down onto the end of the name. Mobile keeps the stacked layout. */
        @media (min-width: 769px) {
          .psh-state-hint--docs {
            left: calc(100% + 8px);
            right: auto;
            top: 50%;
            bottom: auto;
            margin-top: -17px;   /* half the two-line text height */
            align-items: flex-start;
            text-align: left;
            transform-origin: left center;
          }
          .psh-state-hint--docs .psh-state-hint-text { transform: none; white-space: nowrap; }
          .psh-state-hint--docs .psh-state-hint-arrow { display: none; }
          .psh-state-hint--docs .psh-state-hint-arrow-web {
            display: block;
            position: absolute;
            left: -50px;             /* tail over the text, head over the name */
            bottom: calc(100% + 1px);
            width: 72px;
            height: 34px;
          }
        }
        @keyframes psh-hint-pulse {
          0%   { transform: scale(1); }
          10%  { transform: scale(1.12); }
          22%  { transform: scale(1); }
          100% { transform: scale(1); }   /* hold still (delay) before the next pulse */
        }

        @media (max-width: 1024px) {
          .psh-pcard { gap: 24px; padding: 22px 30px 40px; min-height: 0; }
          .psh-pcard-media { flex: 0 0 44%; }
          .psh-pcard-media-img { max-height: 244px; }
          .psh-pcard-media-img--fill { max-height: 278px; }
        }
        @media (max-width: 768px) {
          .psh-section {
            /* Mobile: show the FULL banner (uncropped), scaled to the screen
               width, as a band across the top; dark navy fills the area below it.
               The title / search / trust sit just under the image (.psh-wrap). */
            background-color: #0c2244;
            background-image: url(${mobileHeroImg});
            background-size: contain;          /* whole image, fit to width — no crop */
            background-position: top center;
            background-repeat: no-repeat;
          }
          /* Overlay the title/search card onto the lower part of the full image
             (the desk/foreground area). The image band height is width*563/1000;
             starting the content at ~58% of that drops the card over the desk.
             Tune the 0.58 to move the card up (smaller) or down (larger). */
          .psh-wrap {
            padding-top: calc(100vw * 563 / 1000 * 0.72);
            /* Trim the empty navy strip below the trust stats. */
            padding-bottom: 10px;
          }
          /* Mobile: drop the scrolling carousel + arrows — the image is the bg now. */
          .psh-carousel, .psh-hit { display: none; }
          /* Hide the grid watermark over the photo for a cleaner look. */
          .psh-section::after { display: none; }
          /* Show the curvy, pulsing "select your state" hint on mobile. */
          .psh-state-hint {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: absolute;
            left: 2px;
            bottom: calc(100% + 4px);
            z-index: 46;
            pointer-events: none;
            color: #f0a500;
            transform-origin: bottom left;
            animation: psh-hint-pulse 2.8s ease-in-out infinite;
          }
          .psh-state-hint-text {
            font-size: 11.5px;
            font-weight: 800;
            white-space: nowrap;
            text-shadow: 0 1px 2px rgba(0,0,0,0.45);
          }
          .psh-state-hint-arrow { width: 52px; height: 27px; margin-top: 1px; }
          /* Docs picker: hint on the RIGHT, two-line text, arrow flipped to point
             the other way. Light section, so no dark text-shadow. */
          .psh-state-hint--docs {
            left: auto;
            right: 0;
            bottom: calc(100% - 8px);
            align-items: flex-end;
            text-align: right;
            transform-origin: bottom right;
            max-width: 62vw;   /* never run past the screen edge */
          }
          .psh-state-hint--docs .psh-state-hint-text {
            white-space: normal;
            line-height: 1.1;
            font-size: 11px;
            text-shadow: none;
            /* Rightward nudge, still capped by max-width so it stays on-screen. */
            transform: translate(30px, 4px);
          }
          .psh-state-hint--docs .psh-state-hint-arrow {
            width: 40px;
            height: 20px;
            transform: scaleX(-1);
          }
          /* Small "Start here" tag above the hero state pill. */
          .psh-start-tag {
            display: inline-block;
            position: absolute;
            left: 6px;
            bottom: calc(100% + 6px);
            z-index: 46;
            pointer-events: none;
            background: #f0a500;
            color: #14213d;
            font-size: 9.5px;
            font-weight: 800;
            letter-spacing: 0.02em;
            padding: 2px 9px;
            border-radius: 999px;
            white-space: nowrap;
            box-shadow: 0 5px 12px -5px rgba(0,0,0,0.55);
            transform-origin: bottom left;
            animation: psh-hint-pulse 2.8s ease-in-out infinite;
          }
          /* Little pointer under the tag, aimed at the pill. */
          .psh-start-tag::after {
            content: "";
            position: absolute;
            top: 100%;
            left: 14px;
            border: 5px solid transparent;
            border-top-color: #f0a500;
          }
          .psh-pcard { flex-direction: column; align-items: center; gap: 14px; padding: 18px 18px 38px; text-align: center; }
          .psh-pcard-media { order: -1; flex: none; padding: 0; }
          .psh-pcard-media-img { max-height: 168px; margin: 0 auto; }
          .psh-pcard-media-img--fill { max-height: 196px; }
          .psh-pcard-content { flex: 1 1 auto; align-items: center; }
          .psh-pcard-eyebrow { justify-content: center; }
          .psh-pcard-desc { margin-left: auto; margin-right: auto; }
          /* Force exactly two rows for the four chips, regardless of text
             length, instead of an unpredictable flex-wrap line count. Columns
             size to their own widest chip (not an even 1fr split) so a long
             chip like "Download any document" never has to wrap its text. */
          .psh-pcard-chips {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, max-content));
            justify-content: center;
            justify-items: center;
            gap: 8px;
            width: 100%;
          }
          .psh-pcard-chip {
            font-size: 10px;
            padding: 5px 10px 5px 8px;
            white-space: nowrap;
          }
          .psh-pcard-actions { margin-top: 16px; justify-content: center; }
          .psh-pcard-cta, .psh-pcard-cta2 { justify-content: center; white-space: nowrap; }
          /* Bring the prev/next arrows back on mobile, sized down since they
             now sit on top of the card itself instead of beside it. No
             background/circle — just the bare glyph, same as desktop. */
          .psh-nav {
            display: grid;
            width: 32px; height: 32px;
          }
          .psh-nav-symbol { font-size: 18px; }
          .psh-nav--prev { left: 6px; }
          .psh-nav--next { right: 6px; }
          /* Title + search bar read as one continuous flat white card:
             thin divider, single row of state + search with no boxed
             button — matches the compact mobile search card design. */
          .psh-title {
            border-radius: 16px 16px 0 0;
            box-shadow: none;
            /* No negative overlap on mobile — the static image sits above it. */
            margin-top: 12px;
            padding: 18px 20px 14px;
            font-size: 19.5px;
            /* Phones: let the headline wrap and fill the card width again so it
               never overflows the narrow screen. */
            white-space: normal;
            width: auto;
            max-width: 100%;
          }
          .psh-searchhost { margin-top: 0; }
          .psh-searchbar {
            flex-wrap: nowrap;
            align-items: center;
            gap: 4px;
            padding: 10px 12px;
            min-height: 0;
            border-radius: 0 0 16px 16px;
            border-top: 1px solid #eef1f5;
          }
          .psh-state-btn { border-right: 1px solid #e5e7eb; padding: 0 10px 0 2px; font-size: 14px; }
          .psh-state-btn span { max-width: 84px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .psh-input-wrap { flex: 1 1 auto; min-width: 0; padding: 0 0 0 8px; }
          .psh-input {
            font-size: 16px; /* 16px: iOS zooms into smaller inputs on focus */
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          }
          .psh-search-btn { display: none; }
          .psh-trust { gap: 14px 20px; }
          /* Mobile only (kept on web): hide the slide's question chips and the
             CTA buttons, leaving just the image + title. */
          .psh-pcard-chips,
          .psh-pcard-actions { display: none; }
        }
        @media (max-width: 640px) {
          .psh-trust {
            flex-wrap: nowrap;
            align-items: flex-start;
            justify-content: space-between;
            gap: 0;
            background: transparent;
            box-shadow: none;
            border-radius: 0;
            padding: 0 4px;
            margin: 14px auto 0;
          }
          .psh-trust-item {
            flex: 1 1 0;
            min-width: 0;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            gap: 1px;
            padding: 0 2px;
            text-align: center;
          }
          .psh-trust-icon {
            color: #ffffff;
            width: 15px;
            height: 15px;
            filter: drop-shadow(0 1px 3px rgba(0,0,0,0.6));
          }
          .psh-trust-text { display: none; }
          .psh-trust-value {
            display: block;
            font-size: 11px;
            font-weight: 800;
            color: #ffffff;
            line-height: 1.15;
            text-shadow: 0 1px 3px rgba(0,0,0,0.6);
            white-space: nowrap;
          }
          .psh-trust-label {
            display: block;
            font-size: 8.5px;
            font-weight: 700;
            color: rgba(255, 255, 255, 0.95);
            line-height: 1.2;
            text-shadow: 0 1px 3px rgba(0,0,0,0.6);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .psh-pcard { transition: none; }
          .psh-collapse { transition: none; }
          .psh-state-hint { animation: none; }
          .psh-start-tag { animation: none; }
        }
      `}</style>

      <SampleReportModal
        open={showSample}
        kind={sampleKind}
        onClose={() => setShowSample(false)}
      />

      {referralOpen &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
            <RefferModal
              setShareWindow={() => setReferralOpen(false)}
              encodedShareUrl={referralUrl}
              encodedShareText={`Hey! I've been using JaaGa for property documentation. Super easy and professional! You should try it too! Use my referral link to get ${joiningCredits} JaaGa Credits.`}
            />
          </div>,
          document.body,
        )}

      {/* This section already knows the state and the category being browsed,
          so the map is asked only for the location and the document. */}
      <MapRequestModal
        open={mapRequestOpen}
        onClose={() => setMapRequestOpen(false)}
        fixedState={
          selectedState?.id
            ? { key: String(selectedState.id).toLowerCase(), name: selectedState.name }
            : null
        }
        fixedMode={activeCategory || null}
      />
    </>
  );
};

export default ProductSearchHero;
